import { useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";
import { selectProductData } from "~/domains/product/redux/selectors";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useRegularPnaProductDescriptors } from "~/domains/pna/useRegularPnaProductDescriptors";
import {
  getListPrice,
  getNetPrice,
  getTariff,
  resolveSmallestPackageFromProductData,
} from "~/domains/product/utils/productDescriptorUtils";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import {
  get13DigitCodeForCartAmounts,
  get15DigitCodeForCartAmounts,
} from "~/features/addToCart/redux/cartAmounts/cartAmountsHelper";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import { calculateSalesCampaign4PDP, processPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { selectPriceScalingElement } from "~/utils/pricePicker";
import { zero } from "~/utils/PriceUtils";
import { canSeeListPrice } from "~/utils/features";

/**
 * Price composition mirroring `useSelectPricingDetails`, but computed against this page's own
 * query-built descriptor instead of subscribing to the PnA queries again: net price via
 * `getNetPrice`, cart quantity keyed by the descriptor's own `isMultiPrice`, sales price from
 * the sales-campaign scaling data of the same descriptor. Returns the empty price projection
 * when no basic product is staged.
 */
function composeComparisonPricing(args: {
  base: ProductData | undefined;
  descriptor: ProductDescriptor;
  packCode: string;
  totalQuantity: number;
}) {
  const { base, descriptor, packCode, totalQuantity } = args;
  const netPrice = base ? getNetPrice({ totalQuantity, product: descriptor, packCode }) : null;
  const salesCampaignData = calculateSalesCampaign4PDP({ productDescriptor: descriptor }).data;
  const salesData = salesCampaignData?.salePerPack?.[packCode];
  const salesPrice =
    (salesData?.price ? selectPriceScalingElement(totalQuantity, salesData.price).price : undefined) ?? zero();
  const pricingDetails = processPricingDetails({ netPrice, salesPrice, saleActivated: true });
  const hasSurcharge = (netPrice?.surcharge ?? 0) > 0;
  const showListPrice = base
    ? canSeeListPrice({
        product: descriptor,
        isSpecialPrice: netPrice?.isSpecial ?? false,
        hasSurcharge,
      })
    : false;
  return {
    ...pricingDetails,
    netPrice,
    hasSurcharge,
    showListPrice,
    listPrice: showListPrice ? getListPrice({ product: descriptor, packCode }) : null,
  };
}

export function useProductComparisonCardLogic(productCode: string) {
  const { selection } = usePriceReferenceSelection();
  const comparisonAvailabilityEnabled = useKameleoonFlag(KAMELEOON_FLAGS.comparisonAvailability);
  const priceReference = getInitialPriceReference(selection);
  const base = useSelector(selectProductData(productCode));

  const [productDescriptor] = useRegularPnaProductDescriptors([{ storefrontCode: productCode, base, priceReference }]);

  // Guard: package and pricing resolution need a real base (legacy cards only ever mounted with
  // staged products). The degenerate no-base path returns the empty price projection instead of
  // crashing.
  const packCode = base ? resolveSmallestPackageFromProductData(productDescriptor) : "";
  const cartQtyPerProductCode = useSelector((state: RootState) => state.cartAmounts?.cartQtyPerProductCode);
  const cartQty =
    (productDescriptor.isMultiPrice
      ? cartQtyPerProductCode[get15DigitCodeForCartAmounts(productCode, packCode, priceReference)]
      : cartQtyPerProductCode[get13DigitCodeForCartAmounts(productCode, priceReference)]) || 0;
  const totalQuantity = cartQty + 1;

  const { price, oldPrice, discountFormatted, netPrice, hasSurcharge, showListPrice, listPrice } =
    composeComparisonPricing({ base, descriptor: productDescriptor, packCode, totalQuantity });

  return {
    price,
    oldPrice,
    netPrice,
    discountFormatted,
    productDescriptor,
    showListPrice,
    listPrice,
    packCode,
    tariffSurcharge: isTariffSurchargeEnabled() && getTariff(productDescriptor),
    hasSurcharge,
    comparisonAvailabilityEnabled,
  };
}
